import { Button, useDisclosure } from '@chakra-ui/react'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import _ from 'lodash'
import * as yup from 'yup'
import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '../../components/FormSection'
import { MainContent } from '../../components/MainContent'
import { Save } from '../../icons'
import { ExtraInsuranceCestovko } from '../ExtraInsuranceCestovko'
import { FormButtons } from '../FormButtons'

import type { AxiosError } from 'axios'
import SubmitDialog from 'BrokerTsLib/src/components/SubmitDialog'
import { BadRequestError, type InsuranceParamsTravelDto, type ModelState, type TravelAddonsDto } from 'BrokerTsLib/src/dtos'
import type { DiscountBase, StepBase } from 'BrokerTsLib/src/dtos/StepBase'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import { setModelStateErrors } from 'BrokerTsLib/src/Utils/formModelState'
import { TravelAddonValidationSchema } from 'brokertslib/src/validation'
import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import ErrorSummary from '../../components/ErrorSummary'
import { FormSpacer } from '../../components/FormSpacer'
import { useSaveStep2, useUpdateLastWizardStep } from '../../hooks/useWizard'
import { insuranceParamsContext } from '../../layouts/Wizard'
import { Discounts } from '../discounts/Discounts'

export type Step2FormValues = {
  paramsId: string
  addons: TravelAddonsDto
} & StepBase & DiscountBase

export const schema = yup.object().shape(
  {
    addons: TravelAddonValidationSchema,
  }
)

export const transformInsuranceParamsToStep2FormValues = (dto: InsuranceParamsTravelDto, paramsId?: string) => {
  return {
    paramsId: paramsId,
    addons: { ...dto.addons }
  }
}

export const Step2Form = () => {
  const navigate = useNavigate()
  const { insuranceParams: insuranceParamsGeneric, insuranceParamsId: paramsId, refetchInsuranceParams } = useContext(insuranceParamsContext)!
  const insuranceParams = insuranceParamsGeneric as InsuranceParamsTravelDto

  const [isSubmittingCurrent, setIsSubmittingCurrent] = useState(false)
  const [isSubmittingNext, setIsSubmittingNext] = useState(false)
  const [discountsLoaded, setDiscountsLoaded] = useState(false)

  const formValues = transformInsuranceParamsToStep2FormValues(insuranceParams, paramsId)

  const isContracted = !!insuranceParams.generalParams.contractNumber

  const methods = useForm<Step2FormValues>({
    defaultValues: formValues,
    resolver: yupResolver(schema) as any,
    mode: 'onBlur'
  })

  const {
    handleSubmit,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = methods

  useEffect(() => {
    if (!isSubmitting) {
      setIsSubmittingCurrent(false)
      setIsSubmittingNext(false)
    }
  }, [isSubmitting])

  const { mutateAsync: updateStep } = useUpdateLastWizardStep()
  const { mutateAsync: submitForm } = useSaveStep2()

  const handlePostError = (data: any) => {
    Object.keys(data).forEach((x: any) => {
      if (x.toString().toLowerCase().startsWith('discounts[')) {
        setError(x.toLowerCase(), { message: data[x].join(' ') })
      }
    })
    setModelStateErrors(setError, new BadRequestError(data as ModelState<Step2FormValues>))
  }

  const {
    isOpen: isSavedFormAlertOpen,
    onOpen: savedFormAlertOnOpen,
    onClose: savedFormAlertOnClose,
  } = useDisclosure()

  const onSubmitForm = handleSubmit((data) => {
    const values = _.cloneDeep(data)
    // Datum poslu jen jako date bez casu. To by chtelo vyresit asi nejak obecne. Kdyz se to posle i s casem, tak dochazi k casovemu posunu, coz znamena na serveru predchozi den
    values.addons.vehicleFirstRegistrationDate = formatDate(values.addons.vehicleFirstRegistrationDate)

    return submitForm({ ...values, paramsId: paramsId ?? '' })
      .then((response) => {
        if (data.nextPage == 'next') {
          if ((insuranceParams?.generalParams.lastWizardStep ?? 2) < 3)
            updateStep({ paramsId: Number(paramsId), step: 3 })

          refetchInsuranceParams() // vždy refetch hodnot, jinak se na následujícím kroku zobrazují původní hodnoty 
          navigate(`/cestovko/wizard/3/${paramsId}`)
        } else {
          savedFormAlertOnOpen()
        }
      })
      .catch((err: AxiosError) => {
        if (!(err.response && err.response.status === 400 && err.response.data))
          return

        const data = err.response.data
        handlePostError(data)
      })
      .finally(() => { })
  })

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmitForm}>
        <MainContent>
          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Připojištění</FormSectionTitle>

              <FormSectionDescription>
                Vyberte limity zvoleného připojišťění.
              </FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <ExtraInsuranceCestovko />
            </FormSectionBody>
          </FormSection>

          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Slevy pojištění</FormSectionTitle>

              <FormSectionDescription>
                Nastavte slevy pojistníka a jednotlivých pojišťoven.
              </FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <Discounts onDiscountsLoaded={() => setDiscountsLoaded(true)} />
            </FormSectionBody>
          </FormSection>

          {!isContracted &&
            <FormButtons
              rightButtons={
                <>
                  <Button variant="outline"
                    isDisabled={isSubmitting || isSubmittingNext || isSubmittingCurrent || !discountsLoaded}
                    isLoading={isSubmittingCurrent}
                    loadingText="Ukládám..."
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'current')
                      setIsSubmittingCurrent(true)
                      onSubmitForm()
                    }}>
                    Uložit
                  </Button>

                  <Button
                    colorScheme="primary"
                    isLoading={isSubmittingNext}
                    isDisabled={isSubmitting || isSubmittingNext || isSubmittingCurrent || !discountsLoaded}
                    loadingText="Počítám nabídky..."
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'next')
                      setIsSubmittingNext(true)
                      onSubmitForm()
                    }}>
                    <Save />
                    Uložit a spočítat nabídky
                  </Button>

                  <SubmitDialog
                    isOpen={isSavedFormAlertOpen}
                    onClose={savedFormAlertOnClose}
                    header='Uloženo'
                    body={'Parametry nabídky byly úspěšně uloženy'}
                  />
                </>
              }
            />
          }
        </MainContent>

        <FormSpacer />

        <ErrorSummary errors={errors} />
      </form>
    </FormProvider>
  )
}
